import {
  getMembersVariables,
  PersonalizationValue,
  TriggerStepCreate,
} from "@dots-projects/types";
import { Alert, Select } from "antd";
import TextArea from "antd/lib/input/TextArea";
import produce from "immer";
import { Controller, useForm } from "react-hook-form";
import invariant from "ts-invariant";
import DiscordMemberSingleSelector from "./DiscordSingleMemberSelector";
import { FormLabel } from "./FormLabel";
import { Input } from "./Input";
import { saveDraft, updateStep } from "./reducer/actionCreators";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import { StepFormWithCommonParts } from "./TriggerStepForm";

export default function StepFormKickMember({
  step,
  insertVariables,
}: {
  step: TriggerStepCreate;
  insertVariables: PersonalizationValue[];
}) {
  invariant(step.type === "kick_member");

  const { state, dispatch, onPersist } = useTriggerEditorStateDispatch();
  const form = useForm({
    defaultValues: {
      userId: step.configs.userId,
      name: step.name,
      reason: step.configs.reason,
      delayMinutes:
        typeof step.configs.delaySeconds === "number"
          ? step.configs.delaySeconds / 60
          : 0,
    },
    mode: "onChange",
  });
  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
  } = form;
  const readOnly = !state.trigger.current.isDraft;

  const onSubmit = (data: any) => {
    // This uses the immer library to create a new copy of step (with updates)
    // without affecting the original step.
    const newStep = produce(step, (stepDraft) => {
      stepDraft.name = data.name;
      stepDraft.configs.userId = data.userId;
      stepDraft.configs.reason = data.reason;
      stepDraft.configs.delaySeconds = data.delayMinutes
        ? data.delayMinutes * 60
        : 0;
    });
    dispatch(updateStep(newStep));
    dispatch(saveDraft(onPersist));
  };

  return (
    <StepFormWithCommonParts
      onSubmit={handleSubmit(onSubmit)}
      form={form}
      step={step}
    >
      <Alert
        description="When testing, Dots will not kick the testing member. It will simply skip this step until the flow is published."
        type="warning"
        closable
      />
      <FormLabel label="Which user to apply">
        <DiscordMemberSingleSelector
          form={form}
          members={getMembersVariables(insertVariables)}
          defaultMemberId={step.configs.userId}
          readOnly={readOnly}
          fieldName="userId"
        />
      </FormLabel>
      <FormLabel label="Kick member after">
        <div className="flex w-full	items-end justify-items-center">
          <Input
            // type="number"
            style={{ width: "100%" }}
            readOnly={readOnly}
            {...register("delayMinutes", {
              valueAsNumber: true,
              validate: (value) => value >= 0,
            })}
          />
          <p style={{ margin: "0 0 0 15px" }}> Minutes</p>
        </div>
        {errors.delayMinutes && (
          <p className="text-red-500 m-0">Minimum value is 0</p>
        )}
      </FormLabel>
      <FormLabel label="Reason for kick (optional)">
        <Controller
          control={control}
          name="reason"
          render={({ field: { onChange, onBlur, value } }) => (
            <TextArea
              rows={2}
              style={{
                width: "100%",
                marginTop: "-5px",
                marginBottom: "1rem",
              }}
              className="mb-5"
              readOnly={readOnly}
              onChange={onChange}
              onBlur={onBlur}
              value={value}
            />
          )}
        />
      </FormLabel>
    </StepFormWithCommonParts>
  );
}
